import { ColumnDef } from '@tanstack/react-table';
import { isEmpty } from 'ramda';
import { FC, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { SharingInvitation } from '@/shared/api/protocol-ts/model/dto_sharing_pb';
import { PatientsListColumnName } from '@/shared/config';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Button, Layout, MobileLayout } from '@/shared/ui';

import { accessModel } from '@/entities/access';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import {
  patientModel,
  PatientsFilter,
  PatientsFiltersType,
} from '@/entities/patient';
import { userModel } from '@/entities/user';

import { Footer } from '@/features/footer';
import { RevokeSharedPatientModal } from '@/features/sharePatient';
import { TreatingDoctorsFilterDrawer } from '@/features/treatingDoctors';

import { AboutLabelingModal } from '@/widgets/AboutLabelingModal';
import { GetFullAccessModal } from '@/widgets/GetFullAccessModal';
import { Header, MobileHeader } from '@/widgets/Header';
import { PatientModalForm } from '@/widgets/PatientModalForm';

import { PATIENT_LIST_TOGGLEABLE_COLUMNS } from './config';
import { usePatientListColumnSettings } from './hooks';
import { usePatientsSortAndFilters } from './hooks/usePatientsSortAndFilters';
import { useSharedPatientColumnsScheme } from './hooks/useSharedPatientColumnsScheme';
import styles from './Patients.module.scss';
import { ChangeOrganizationModal } from './ui/ChangeOrganizationModal/ChangeOrganizationModal';
import { MobilePatientList } from './ui/MobilePatientList/MobilePatientList';
import { MobileSharedPatientList } from './ui/MobileSharedPatientList/MobileSharedPatientList.tsx';
import { PatientList } from './ui/PatientList/PatientList';
import { PatientListControls } from './ui/PatientListControls/PatientListControls';
import { PatientListSearch } from './ui/PatientListSearch/PatientListSearch';
import { PatientsFilters } from './ui/PatientsFilters/PatientsFilters';
import { SharedPatientList } from './ui/SharedPatientList/SharedPatientList';

export const Patients: FC = () => {
  const [searchParams] = useSearchParams();

  const { formatMessage } = useIntl();

  const location = useLocation();

  const { isCurrentUserAdmin } = useAppSelector(
    userModel.selectors.selectUserOrganizationLegacy,
  );
  const organizationLoading = useAppSelector(
    organizationModel.selectors.selectLoading,
  );

  // type casting is necessary for typification location.state
  const invitationOrganizationID = (
    location?.state as { invitationOrganizationID: string }
  )?.invitationOrganizationID;

  const invitationOrganizationName = (
    location?.state as { invitationOrganizationName: string }
  )?.invitationOrganizationName;

  const organizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );

  const allOrganizations = useAppSelector(
    organizationModel.selectors.organizationSelectors.selectAll,
  );

  const { sharedWithMeCount, sharedByMeCount, totalOrganizationPatientCount } =
    useAppSelector(patientModel.selectors.selectCounters);

  const {
    accessSharedWithMeCount,
    accessSharedByMeCount,
    accessTotalOrganizationPatientCount,
  } = useAppSelector(accessModel.selectors.selectCounters);

  const dispatch = useAppDispatch();

  const { isPhone, isMobile } = useMedia();

  const isForbidPatientEditing = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_PatientEditing,
    ),
  );

  const defaultPatientsFilter = isEmpty(allOrganizations)
    ? PatientsFilter.sharedWithMe
    : PatientsFilter.all;

  const patientsFilter =
    (searchParams.get('filters') as PatientsFiltersType) ??
    defaultPatientsFilter;

  //  If there are no organizations, the only tab available will be 'Shared with Me'
  const isAllPatientList =
    (patientsFilter === PatientsFilter.all && !isEmpty(allOrganizations)) ||
    (patientsFilter === null && !isEmpty(allOrganizations));

  const isSharedWithMeList =
    patientsFilter === PatientsFilter.sharedWithMe || isEmpty(allOrganizations);

  const isSharedByMeOrSharedByStaffList =
    patientsFilter === PatientsFilter.sharedByMe ||
    patientsFilter === PatientsFilter.sharedByStaff;

  const patientCounts = isAllPatientList
    ? {
        sharedWithMeCount,
        sharedByMeCount,
        totalOrganizationPatientCount,
      }
    : {
        sharedWithMeCount: accessSharedWithMeCount,
        sharedByMeCount: accessSharedByMeCount,
        totalOrganizationPatientCount: accessTotalOrganizationPatientCount,
      };

  const { sharedByMeColumnsScheme, sharedWithMeColumnsScheme } =
    useSharedPatientColumnsScheme();

  const {
    columnVisibility,
    columnSizing,
    toggleColumn,
    setColumnWidths,
    reset: resetColumnSettings,
  } = usePatientListColumnSettings(organizationID);

  // Column settings drive the desktop table only, so the mobile list keeps
  // every filter it was opened with.
  const hiddenColumns = isMobile
    ? []
    : PATIENT_LIST_TOGGLEABLE_COLUMNS.filter(
        (column) => columnVisibility[column] === false,
      );

  const {
    sortByForSharedWithMe,
    sortByForSharedByMe,
    sortByForAllPatients,
    searchPatients,
    filterByDoctors,
    onFilterPatientsChange,
    fetchMorePatients,
    filterByStudyTypes,
    filterByAgeGroup,
    filterByLastModified,
    clearColumnFilter,
  } = usePatientsSortAndFilters({
    organizationID,
    isSharedWithMeList,
    isSharedByMeOrSharedByStaffList,
    isAllPatientList,
    hiddenColumns,
  });

  const toggleColumnVisibility = (column: PatientsListColumnName) => {
    if (columnVisibility[column] !== false) {
      clearColumnFilter(column);
    }

    toggleColumn(column);
  };

  const openNewPatientModalForm = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.PatientModalForm,
        data: {
          patientID: '',
        },
      }),
    );
  };

  useEffect(() => {
    if (organizationLoading !== 'pending') {
      trackEvent({
        eventName: 'Patients Page Viewed',
        properties: { filter: patientsFilter },
        isPageViewEvent: true,
      });
    }
  }, [organizationLoading]);

  return isMobile ? (
    <MobileLayout>
      <MobileHeader />

      <MobileLayout.Main>
        <PatientsFilters
          amount={patientCounts}
          onFilterChange={(value) =>
            onFilterPatientsChange(value as PatientsFiltersType)
          }
        />
        <div className={styles.mobileSearch}>
          <PatientListSearch
            onInputChange={searchPatients}
            currentPatientsFilter={patientsFilter}
          />
          {!isForbidPatientEditing && (
            <Button
              data-component="create-new-patient-button"
              icon="plus"
              size="large"
              onClick={openNewPatientModalForm}
            />
          )}
        </div>

        {isAllPatientList && (
          <MobilePatientList
            organizationID={organizationID}
            reFetchPatients={fetchMorePatients}
            onAddPatient={openNewPatientModalForm}
          />
        )}

        {isSharedWithMeList && (
          <MobileSharedPatientList
            organizationID={organizationID}
            reFetchPatients={fetchMorePatients}
          />
        )}

        {isSharedByMeOrSharedByStaffList && (
          <MobileSharedPatientList
            isSharedByMeOrSharedByStaffList
            organizationID={organizationID}
            reFetchPatients={fetchMorePatients}
          />
        )}
      </MobileLayout.Main>

      <PatientModalForm />
      <RevokeSharedPatientModal />
      <TreatingDoctorsFilterDrawer onSave={filterByDoctors} />
    </MobileLayout>
  ) : (
    <Layout>
      <Layout.Header>
        <Header />
      </Layout.Header>

      <Layout.Content>
        <Layout.Main>
          <section className={styles.container} data-section="patients">
            <main className={styles.main}>
              <div className={styles.header}>
                <h1 className="h1">
                  <FormattedMessage
                    id="patientList.pageTitle"
                    defaultMessage="Patients"
                  />
                </h1>
                {!isForbidPatientEditing && (
                  <Button
                    data-component="create-new-patient-button"
                    className={styles.newPatientButton}
                    onClick={openNewPatientModalForm}
                    icon="plus"
                    size={isPhone ? 'medium' : 'large'}
                  >
                    {isMobile ? (
                      <FormattedMessage
                        id="patientList.addNew"
                        defaultMessage="Add new"
                      />
                    ) : (
                      <FormattedMessage
                        id="patientList.addPatient"
                        defaultMessage="Add new patient"
                      />
                    )}
                  </Button>
                )}
              </div>

              <PatientListControls
                onInputChange={searchPatients}
                onFilterChange={(value) =>
                  onFilterPatientsChange(value as PatientsFiltersType)
                }
                amount={patientCounts}
                currentPatientsFilter={patientsFilter}
              />

              {isAllPatientList && (
                <PatientList
                  handleAddPatient={openNewPatientModalForm}
                  reFetchPatients={fetchMorePatients}
                  sortBy={sortByForAllPatients}
                  onDoctorFilterChange={filterByDoctors}
                  onStudyTypesFilterChange={filterByStudyTypes}
                  onAgeGroupFilterChange={filterByAgeGroup}
                  onLastModifiedFilterChange={filterByLastModified}
                  columnVisibility={columnVisibility}
                  columnSizing={columnSizing}
                  onColumnSizingChange={setColumnWidths}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetColumnSettings}
                />
              )}

              {isSharedWithMeList && (
                <SharedPatientList
                  showSharedByColumn={isCurrentUserAdmin}
                  sharedPatientColumnsScheme={
                    sharedWithMeColumnsScheme as ColumnDef<SharingInvitation>[]
                  }
                  sharedPatientCounter={accessSharedWithMeCount}
                  sortBy={sortByForSharedWithMe}
                  reFetchPatients={fetchMorePatients}
                  emptyText={formatMessage({
                    id: 'patientList.emptySharedWithMe',
                    defaultMessage: 'No one has shared a patient with you yet',
                  })}
                />
              )}

              {isSharedByMeOrSharedByStaffList && (
                <SharedPatientList
                  sharedPatientColumnsScheme={
                    sharedByMeColumnsScheme as ColumnDef<SharingInvitation>[]
                  }
                  sharedPatientCounter={accessSharedByMeCount}
                  sortBy={sortByForSharedByMe}
                  reFetchPatients={fetchMorePatients}
                  emptyText={formatMessage({
                    id: 'patientList.emptySharedByMeOrTeam',
                    defaultMessage: 'You have not shared any patients yet',
                  })}
                />
              )}
            </main>
          </section>
        </Layout.Main>
      </Layout.Content>

      <Layout.Footer>
        <Footer />
      </Layout.Footer>

      <AboutLabelingModal />

      <ChangeOrganizationModal
        invitationOrganizationID={invitationOrganizationID}
        invitationOrganizationName={invitationOrganizationName}
      />
      <AboutLabelingModal />
      <GetFullAccessModal />
      <PatientModalForm />
      <RevokeSharedPatientModal />
    </Layout>
  );
};
